import { Building2, Loader2 } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { DialogClose, DialogContent } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

import {
  toPersonPickerOption,
  type ClientMember,
  type ClientRow,
  type ClientUserOption,
  type CreateClientInput,
  type StaffOption,
  type UserId,
} from "./clients-data"
import { useNewEngagementDraft } from "./hooks/use-new-engagement-draft"

// Names the reviews an engagement already has, for a count of one or more:
// "the 1 review" or "all 5 reviews". A count of 0 needs its own sentence.
const existingReviews = (count: number): string =>
  count === 1 ? "the 1 review" : `all ${String(count)} reviews`

export function GrantAccessDialog({
  engagement,
  user,
  onConfirm,
  isSubmitting = false,
}: {
  engagement: ClientRow
  user: ClientUserOption
  onConfirm: () => void
  isSubmitting?: boolean
}): JSX.Element {
  return (
    <DialogContent
      title={`Grant ${user.name} access?`}
      description={`They will be added as a member of ${engagement.name}.`}
      size="sm"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            loading={isSubmitting}
            loadingText="Granting…"
            onClick={onConfirm}
          >
            Grant access
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-body-sm leading-normal text-text-2">
        {engagement.reviewCount === 0 ? (
          <p>
            They will see <span className="font-semibold text-text">any future reviews</span> under
            this engagement — client access is to the engagement, not to individual reviews.
          </p>
        ) : (
          <p>
            They will see{" "}
            <span className="font-semibold text-text">
              {existingReviews(engagement.reviewCount)}
            </span>{" "}
            under this engagement, including every report already sent — client access is to the
            engagement, not to individual reviews.
          </p>
        )}
        <div className="flex items-center gap-2.5 rounded-md border border-border-soft bg-surface-sunken px-3 py-2">
          <Avatar name={user.name} identity={user.id} size="sm" />
          <div className="min-w-0">
            <div className="truncate text-body-sm font-medium text-text">{user.name}</div>
            <div className="truncate font-id text-caption text-text-3">{user.email}</div>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}

export function RevokeAccessDialog({
  engagement,
  member,
  onConfirm,
  isSubmitting = false,
}: {
  engagement: ClientRow
  member: ClientMember
  onConfirm: () => void
  isSubmitting?: boolean
}): JSX.Element {
  return (
    <DialogContent
      title={`Revoke ${member.name}'s access?`}
      description="Their account stays — only this engagement is revoked."
      size="sm"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            size="sm"
            className="border-err bg-err/10 text-err hover:bg-err/15"
            loading={isSubmitting}
            loadingText="Revoking…"
            onClick={onConfirm}
          >
            Revoke access
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-body-sm leading-normal text-text-2">
        {engagement.reviewCount === 0 ? (
          <p>
            They lose access to <span className="font-semibold text-text">{engagement.name}</span>{" "}
            on their next page load.
          </p>
        ) : (
          <p>
            They lose access to{" "}
            <span className="font-semibold text-text">
              {existingReviews(engagement.reviewCount)}
            </span>{" "}
            under {engagement.name} on their next page load.
          </p>
        )}
        {member.isPrimary && (
          <p className="rounded-md border border-print/40 bg-print-tint/60 px-3 py-2 text-body-sm text-text">
            <span className="font-semibold">{member.name}</span> is marked as the client&apos;s main
            contact. Revoking their access also removes that mark.
          </p>
        )}
      </div>
    </DialogContent>
  )
}

const CLIENT_NAME_MAX_LENGTH = 200

const NO_CLIENT_ACCESS_UNTIL_GRANTED =
  "Nobody at the client can see it until you grant a contact access."

const NEW_REVIEWS_ARRIVE_UNASSIGNED = "New reviews will arrive unassigned."

export function NewEngagementDialog({
  primaryClientContactCandidates,
  onSubmit,
}: {
  primaryClientContactCandidates: readonly StaffOption[]
  onSubmit: (draft: CreateClientInput) => Promise<void>
}): JSX.Element {
  const {
    draft,
    fieldErrors,
    isCreating,
    canSubmit,
    changeName,
    changePrimaryClientContact,
    changeNotes,
    submitDraft,
  } = useNewEngagementDraft(onSubmit)

  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    void submitDraft()
  }

  const primaryClientContactOptions = primaryClientContactCandidates.map(toPersonPickerOption)

  const footerConsequence =
    draft.primaryClientContactId === null
      ? `${NO_CLIENT_ACCESS_UNTIL_GRANTED} ${NEW_REVIEWS_ARRIVE_UNASSIGNED}`
      : NO_CLIENT_ACCESS_UNTIL_GRANTED

  return (
    <DialogContent
      title="New engagement"
      description="Sets the boundary that scopes a client login to its own reviews."
      size="md"
      footer={
        <>
          <span className="mr-auto text-caption text-text-3">{footerConsequence}</span>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={isCreating}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={isCreating ? <Loader2 className="animate-spin" /> : <Building2 />}
            disabled={!canSubmit}
            onClick={submit}
          >
            {isCreating ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <FormField label="Client name" required error={fieldErrors.name}>
          <Input
            value={draft.name}
            onChange={(e) => {
              changeName(e.currentTarget.value)
            }}
            placeholder="Acme Crop Science"
            maxLength={CLIENT_NAME_MAX_LENGTH}
            autoFocus
          />
        </FormField>

        <FormField
          label="Primary Client Contact"
          optional
          hint="New reviews for this engagement are assigned to this person. Can be set later."
          error={fieldErrors.primaryClientContact}
        >
          <Combobox
            options={primaryClientContactOptions}
            value={draft.primaryClientContactId}
            onValueChange={(pickedUserId) => {
              changePrimaryClientContact(pickedUserId as UserId | null)
            }}
            placeholder="Search staff"
            searchPlaceholder="Search by name or email"
          />
        </FormField>

        <FormField
          label="Notes"
          optional
          hint="Engagement scope, portfolio, anything the review team should know."
        >
          <Textarea
            minRows={2}
            value={draft.notes}
            onChange={(e) => {
              changeNotes(e.currentTarget.value)
            }}
            placeholder="Optional context for the review team"
          />
        </FormField>
      </form>
    </DialogContent>
  )
}
